/**
 * The library as the workspace needs it.
 *
 * Two things come from the server: what can be placed (each template's latest
 * version, for the palette) and what the design already pins (possibly older
 * versions, for drawing and inspecting). They are loaded into one
 * `SceneLibrary` keyed by version id, because the scene model resolves by
 * version and a pinned version is never silently swapped for the latest
 * (requirement 4.8).
 *
 * Listing then fetching each latest version is one request per template. With
 * the seeded library at fifteen templates that is trivially cheap (design
 * §1.4); a projection is the answer if it ever is not.
 */
import { api, type LibraryEntity, type LibraryVersion } from "../api/client";
import {
  servesKind,
  type DesignDocument,
  type DesignKind,
  type GroupDefinitionDocument,
  type NodeRole,
  type TemplateDocument,
} from "../design/document";
import type { DefinitionEntry, SceneLibrary } from "../scene/types";

export interface VersionInfo {
  versionId: string;
  entityId: string;
  name: string;
  number: number;
  /** The entity's latest version, for "v1 pinned · v2 available". */
  latestVersionId: string | null;
  latestNumber: number | null;
  /** A project's own variant rather than the shared library's. */
  projectScoped: boolean;
}

export interface WorkspaceLibrary {
  scene: SceneLibrary;
  /** Every loaded template or block definition version, by version id. */
  versions: Readonly<Record<string, VersionInfo>>;
  /** Latest version id of each template entity: what the palette offers. */
  latestTemplates: readonly string[];
  /** Latest version id of each block definition the design may place (D20). */
  latestBlocks: readonly string[];
  /** Pinned versions that could not be loaded; the scene draws a fallback. */
  unavailable: readonly string[];
}

export const EMPTY_WORKSPACE_LIBRARY: WorkspaceLibrary = {
  scene: { templates: {}, definitions: {} },
  versions: {},
  latestTemplates: [],
  latestBlocks: [],
  unavailable: [],
};

/** Every template and block definition version a design refers to. */
export function pinnedVersions(document: DesignDocument): { templates: string[]; definitions: string[] } {
  const templates = new Set<string>();
  for (const node of document.nodes ?? []) if (node.template_version_id) templates.add(node.template_version_id);
  for (const edge of document.edges ?? []) if (edge.template_version_id) templates.add(edge.template_version_id);
  const definitions = new Set((document.group_instances ?? []).map((instance) => instance.group_definition_version_id));
  return { templates: [...templates].sort(), definitions: [...definitions].sort() };
}

function info(version: LibraryVersion, entities: ReadonlyMap<string, LibraryEntity>): VersionInfo {
  const entity = entities.get(version.entity_id);
  return {
    versionId: version.id,
    entityId: version.entity_id,
    name: entity?.name ?? "Unlisted entity",
    number: version.number,
    latestVersionId: entity?.latest_version_id ?? null,
    latestNumber: entity?.latest_version_number ?? null,
    projectScoped: entity ? !entity.is_library_scoped : false,
  };
}

async function fetchVersions(ids: readonly string[]): Promise<{ loaded: LibraryVersion[]; failed: string[] }> {
  const results = await Promise.allSettled(ids.map((id) => api.getLibraryVersion(id)));
  const loaded: LibraryVersion[] = [];
  const failed: string[] = [];
  results.forEach((result, index) => {
    if (result.status === "fulfilled") loaded.push(result.value);
    else failed.push(ids[index]!);
  });
  return { loaded, failed };
}

/**
 * Load the palette's templates and everything `document` pins.
 *
 * A pinned version that fails to load is reported rather than thrown: the
 * design still opens, the node draws as a fallback, and the checker says the
 * template was unavailable.
 */
export async function loadWorkspaceLibrary(projectId: string | null, document: DesignDocument): Promise<WorkspaceLibrary> {
  const [templateEntities, blockEntities] = await Promise.all([
    api.listLibrary("templates", projectId ?? undefined),
    api.listLibrary("blocks", projectId ?? undefined),
  ]);
  const entities = new Map([...templateEntities, ...blockEntities].map((entity) => [entity.id, entity]));
  const latest = (list: readonly LibraryEntity[]) =>
    list.map((entity) => entity.latest_version_id).filter((id): id is string => !!id);
  const latestTemplates = latest(templateEntities);
  const latestBlocks = latest(blockEntities);
  return extendLibrary(
    { ...EMPTY_WORKSPACE_LIBRARY, latestTemplates, latestBlocks },
    document,
    entities,
    [...latestTemplates, ...latestBlocks],
  );
}

/**
 * List the blocks again, after one is saved or a definition is published:
 * the palette offers each one's latest version, and every loaded version of
 * a block learns what its entity's latest now is.
 */
export async function reloadBlocks(library: WorkspaceLibrary, projectId: string): Promise<WorkspaceLibrary> {
  const blockEntities = await api.listLibrary("blocks", projectId);
  const latestBlocks = blockEntities.map((entity) => entity.latest_version_id).filter((id): id is string => !!id);
  const byEntity = new Map(blockEntities.map((entity) => [entity.id, entity]));
  const versions: Record<string, VersionInfo> = {};
  for (const [id, entry] of Object.entries(library.versions)) {
    const entity = byEntity.get(entry.entityId);
    versions[id] = entity
      ? { ...entry, name: entity.name, latestVersionId: entity.latest_version_id ?? null, latestNumber: entity.latest_version_number ?? null }
      : entry;
  }
  return extendLibrary({ ...library, versions, latestBlocks }, { nodes: [] }, byEntity, latestBlocks);
}

/**
 * Load whatever `document` pins that `library` does not hold yet: after
 * adopting someone else's draft, for example, which may pin versions this
 * session has never seen.
 */
/**
 * A template with its numeric defaults as numbers. Versions seeded before the
 * library read `3.2e5` as a number hold such defaults as text, and a stored
 * version is never rewritten (invariant 1); the server reads them as numbers
 * (D55), and so does everything here.
 */
export function numericDefaults(template: TemplateDocument): TemplateDocument {
  const parameters = template.parameters;
  if (!parameters?.some((parameter) => typeof parameter.default === "string" && (parameter.value_type === "float" || parameter.value_type === "int"))) {
    return template;
  }
  return {
    ...template,
    parameters: parameters.map((parameter) => {
      if (typeof parameter.default !== "string" || (parameter.value_type !== "float" && parameter.value_type !== "int")) return parameter;
      const number = Number(parameter.default);
      return parameter.default.trim() !== "" && Number.isFinite(number) ? { ...parameter, default: number } : parameter;
    }),
  };
}

export async function extendLibrary(
  library: WorkspaceLibrary,
  document: DesignDocument,
  entities: ReadonlyMap<string, LibraryEntity> = new Map(),
  extraTemplates: readonly string[] = [],
): Promise<WorkspaceLibrary> {
  const pinned = pinnedVersions(document);
  const wanted = [...new Set([...extraTemplates, ...pinned.templates, ...pinned.definitions])].filter(
    (id) => !library.versions[id],
  );
  if (wanted.length === 0) return library;

  const { loaded, failed } = await fetchVersions(wanted);
  const templates: Record<string, TemplateDocument> = { ...library.scene.templates };
  const definitions: Record<string, DefinitionEntry> = { ...library.scene.definitions };
  const versions: Record<string, VersionInfo> = { ...library.versions };
  // Keep names already known for entities this call was not given.
  const known = new Map(entities);
  for (const existing of Object.values(library.versions)) {
    if (!known.has(existing.entityId)) {
      known.set(existing.entityId, {
        id: existing.entityId,
        name: existing.name,
        latest_version_id: existing.latestVersionId,
        latest_version_number: existing.latestNumber,
        is_library_scoped: !existing.projectScoped,
      } as LibraryEntity);
    }
  }
  for (const version of loaded) {
    const entry = info(version, known);
    versions[version.id] = entry;
    if (version.kind === "templates") {
      templates[version.id] = numericDefaults(version.content as unknown as TemplateDocument);
    } else if (version.kind === "blocks") {
      definitions[version.id] = { name: entry.name, content: version.content as unknown as GroupDefinitionDocument };
    }
  }
  return {
    scene: { templates, definitions },
    versions,
    latestTemplates: library.latestTemplates,
    latestBlocks: library.latestBlocks,
    unavailable: [...new Set([...library.unavailable, ...failed])],
  };
}

// --- Palette -----------------------------------------------------------------

export interface PaletteItem {
  versionId: string;
  name: string;
  glyph: string;
  role: string;
  projectScoped: boolean;
}

export interface PaletteGroup {
  label: string;
  items: PaletteItem[];
}

/**
 * Palette sections by role (requirement 13.1). Roles that behave alike for a
 * designer share a section: a passive valve sits with the valves even though
 * scripts treat it differently (D24). A role not listed still appears, under
 * its own name, so a new role never vanishes from the palette.
 */
const SECTIONS: readonly { label: string; roles: readonly NodeRole[] }[] = [
  { label: "Ports", roles: ["port"] },
  { label: "Connectors", roles: ["aseptic_connector", "coupling"] },
  { label: "Valves", roles: ["valve", "passive_valve"] },
  { label: "Sources", roles: ["reservoir"] },
  { label: "Pumps", roles: ["pump"] },
  { label: "Air supply", roles: ["supply", "regulator"] },
  { label: "Junctions", roles: ["channel_junction"] },
  { label: "Chambers", roles: ["chamber", "variable_volume_chamber"] },
  { label: "Mixers", roles: ["mixer"] },
  { label: "Filters", roles: ["filter"] },
  { label: "Sensors", roles: ["sensor"] },
  { label: "Vents", roles: ["vent"] },
];

function sentence(role: string): string {
  const words = role.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The node templates the palette offers, grouped. Edge templates are not
 * tiles: a channel is made by connecting ports, and the connector tool's
 * selector offers those. Only templates for this kind of design (D34).
 */
export function paletteGroups(
  library: WorkspaceLibrary,
  search = "",
  kind: DesignKind = "microfluidic",
): PaletteGroup[] {
  const needle = search.trim().toLowerCase();
  const items: PaletteItem[] = [];
  for (const versionId of library.latestTemplates) {
    const template = library.scene.templates[versionId];
    const entry = library.versions[versionId];
    if (!template || !entry || template.element === "edge" || !servesKind(template, kind)) continue;
    if (needle && !`${entry.name} ${template.role}`.toLowerCase().includes(needle)) continue;
    items.push({
      versionId,
      name: entry.name,
      glyph: template.symbol.glyph,
      role: template.role,
      projectScoped: entry.projectScoped,
    });
  }
  items.sort((a, b) => a.name.localeCompare(b.name));

  const groups: PaletteGroup[] = [];
  const placed = new Set<string>();
  for (const section of SECTIONS) {
    const members = items.filter((item) => (section.roles as readonly string[]).includes(item.role));
    members.forEach((item) => placed.add(item.versionId));
    if (members.length > 0) groups.push({ label: section.label, items: members });
  }
  const rest = new Map<string, PaletteItem[]>();
  for (const item of items) {
    if (placed.has(item.versionId)) continue;
    rest.set(item.role, [...(rest.get(item.role) ?? []), item]);
  }
  for (const [role, members] of [...rest].sort(([a], [b]) => a.localeCompare(b))) {
    groups.push({ label: sentence(role), items: members });
  }
  return groups;
}

/**
 * The blocks the palette offers: each definition's latest version, placed as
 * an instance through the server (requirement 8.3).
 */
export function paletteBlocks(library: WorkspaceLibrary, search = ""): PaletteItem[] {
  const needle = search.trim().toLowerCase();
  const items: PaletteItem[] = [];
  for (const versionId of library.latestBlocks) {
    const definition = library.scene.definitions[versionId];
    const entry = library.versions[versionId];
    if (!definition || !entry) continue;
    if (needle && !`${entry.name} block`.toLowerCase().includes(needle)) continue;
    items.push({
      versionId,
      name: entry.name,
      glyph: definition.content.symbol?.glyph ?? "block",
      role: "block",
      projectScoped: entry.projectScoped,
    });
  }
  return items.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * What a component could change to in place: the latest template of each
 * other entity with the same role, for this kind of design (D34), by name.
 */
export function swapCandidates(
  library: WorkspaceLibrary,
  role: string,
  currentVersionId: string | null | undefined,
  kind: DesignKind = "microfluidic",
): { versionId: string; name: string }[] {
  const currentEntity = currentVersionId ? library.versions[currentVersionId]?.entityId : undefined;
  return library.latestTemplates
    .filter((id) => {
      const template = library.scene.templates[id];
      const entry = library.versions[id];
      return (
        template !== undefined &&
        entry !== undefined &&
        (template.element ?? "node") === "node" &&
        template.role === role &&
        servesKind(template, kind) &&
        entry.entityId !== currentEntity
      );
    })
    .map((id) => ({ versionId: id, name: library.versions[id]!.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Edge templates, for the connector tool's channel selector. */
export function connectorTemplates(
  library: WorkspaceLibrary,
  kind: DesignKind = "microfluidic",
): { versionId: string; name: string }[] {
  return library.latestTemplates
    .filter((id) => {
      const template = library.scene.templates[id];
      return template?.element === "edge" && servesKind(template, kind);
    })
    .map((id) => ({ versionId: id, name: library.versions[id]?.name ?? id }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The latest T junction, for branching a line (BRANCH_EDGE). The editor picks
 * one on its own, but can only see versions; the workspace knows which is
 * latest, so a branch never uses an old pinned version by accident.
 */
/** The latest air line template (D30), for lines to gas ports. */
export function latestAirLine(library: WorkspaceLibrary, kind: DesignKind = "microfluidic"): string | null {
  return (
    library.latestTemplates.find((id) => {
      const template = library.scene.templates[id];
      return template?.role === "air_line" && servesKind(template, kind);
    }) ?? null
  );
}

/** Whether an edge template is pump tubing (D48): for the length through a pump head, not a default line. */
function isPumpTubing(template: TemplateDocument | undefined): boolean {
  return template?.parameters?.some((parameter) => parameter.key === "grade" && parameter.default === "peristaltic_pump") ?? false;
}

/**
 * The latest channel template for this kind of design: what the connector
 * starts on. General tubing rather than pump tubing, which is chosen on
 * purpose for the one length that runs through a head.
 */
export function latestConnector(library: WorkspaceLibrary, kind: DesignKind = "microfluidic"): string | null {
  const edges = connectorTemplates(library, kind).map((entry) => entry.versionId);
  const channel = (id: string) => library.scene.templates[id]?.role === "channel";
  return (
    edges.find((id) => channel(id) && !isPumpTubing(library.scene.templates[id])) ??
    edges.find(channel) ??
    edges[0] ??
    null
  );
}

export function latestJunction(library: WorkspaceLibrary, kind: DesignKind = "microfluidic"): string | null {
  const junctions = library.latestTemplates.filter((id) => {
    const template = library.scene.templates[id];
    return template?.role === "channel_junction" && template !== undefined && servesKind(template, kind);
  });
  const glyph = (name: string) => junctions.find((id) => library.scene.templates[id]?.symbol.glyph === name);
  return glyph("junction") ?? glyph("t_junction") ?? junctions[0] ?? null;
}

/** Which kind of design a profile's manufacturing approach checks (D34). */
export function kindOfApproach(approach: unknown): DesignKind {
  return approach === "tubing_assembly" ? "tubing_set" : "microfluidic";
}

/**
 * The profiles a design of this kind can be checked against. Reading each
 * one's approach costs a request per profile, and there are a handful. One that
 * cannot be read is kept: the server refuses a mismatch in any case.
 */
export async function profilesForKind(profiles: readonly LibraryEntity[], kind: DesignKind): Promise<LibraryEntity[]> {
  const read = await Promise.allSettled(
    profiles.map((profile) =>
      profile.latest_version_id ? api.getLibraryVersion(profile.latest_version_id) : Promise.reject(new Error("unpublished")),
    ),
  );
  return profiles.filter((_, index) => {
    const result = read[index]!;
    return result.status === "rejected" || kindOfApproach(result.value.content.approach) === kind;
  });
}
